import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Pill, StatusBadge } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { Card, ErrorBox, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { useFeedback } from "../components/Feedback";
import { api, type DocSummary } from "../lib/api";
import { day, money, num } from "../lib/format";
import { useApi } from "../lib/useApi";

export const DOC_TYPES = [
  { id: "sales_invoice", label: "Sales invoice", plural: "Sales invoices" },
  { id: "transfer_invoice", label: "Transfer invoice", plural: "Transfer invoices" },
  { id: "purchase_order", label: "Purchase order", plural: "Purchase orders" },
] as const;

export function Documents() {
  const all = useApi<DocSummary[]>("/documents");
  const tabs = [
    { id: "all", label: "All", count: all.data?.length },
    ...DOC_TYPES.map((t) => ({ id: t.id, label: t.plural, count: all.data?.filter((d) => d.doc_type === t.id).length })),
  ];
  const [tab, setTab] = useTab(tabs, "type");
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";

  const practice = (all.data ?? []).filter((d) => d.practice).length;
  const [clearing, setClearing] = useState(false);
  const { confirm } = useFeedback();
  const clear = async () => {
    if (!(await confirm({ title: `Delete all ${practice} practice document${practice === 1 ? "" : "s"}?`,
                          body: "Real documents are not touched.", confirm: "Delete", danger: true }))) return;
    setClearing(true);
    try { await api.del("/documents/practice"); all.reload(); } finally { setClearing(false); }
  };

  const rows = (all.data ?? []).filter((d) => (tab === "all" || d.doc_type === tab) && (!status || d.status === status));

  return (
    <>
      <PageHeader title="Documents" sub="Invoices, transfers and purchase orders. Issuing a document never changes stock."
        actions={DOC_TYPES.map((t) => (
          <Link key={t.id} className="btn btn-primary" to={`/documents/new?type=${t.id}`}>New {t.label.toLowerCase()}</Link>
        ))} />
      <Card title="Practice">
        <p className="note" style={{ marginTop: 0 }}>
          Try a purchase order, invoice or transfer from start to finish without touching real records: a practice
          document is marked PRACTICE, uses no real number, never changes stock in transit and can be deleted any time.
        </p>
        <div className="form-actions">
          {DOC_TYPES.map((t) => (
            <Link key={t.id} className="btn" to={`/documents/new?type=${t.id}&practice=1`}>Practice {t.label.toLowerCase()}</Link>
          ))}
          {practice > 0 && (
            <button className="btn btn-ghost-danger" disabled={clearing} onClick={() => void clear()}>
              Clear {practice} practice document{practice === 1 ? "" : "s"}
            </button>
          )}
        </div>
      </Card>
      <Tabs tabs={tabs} current={tab} onChange={setTab} />
      {all.error && <ErrorBox error={all.error} onRetry={all.reload} />}
      <Card>
        {!all.data ? <Loading /> : (
          <DataTable<DocSummary>
            testId="documents-table" rows={rows} rowKey={(d) => d.id} href={(d) => `/documents/${d.id}`}
            filterPlaceholder="Search number, customer, vendor…"
            empty="No documents yet — create one with the buttons above."
            toolbar={
              <select aria-label="Status" value={status} onChange={(e) => {
                const n = new URLSearchParams(params); if (e.target.value) n.set("status", e.target.value); else n.delete("status"); setParams(n, { replace: true });
              }}>
                <option value="">Any status</option>
                {["draft", "issued", "shipped", "received", "cancelled"].map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            }
            columns={[
              { key: "number", label: "Number", render: (d) => <><Link to={`/documents/${d.id}`}><strong>{d.number}</strong></Link>{d.practice && <> <Pill tone="info">practice</Pill></>}</> },
              { key: "type_label", label: "Type" },
              { key: "status", label: "Status", render: (d) => <StatusBadge status={d.status} docType={d.doc_type} /> },
              { key: "issue_date", label: "Date", render: (d) => day(d.issue_date) },
              { key: "counterparty", label: "To / from", render: (d) => d.counterparty ?? "—" },
              { key: "total_qty", label: "Qty", numeric: true, render: (d) => num(d.total_qty) },
              { key: "total_amount", label: "Total", numeric: true, render: (d) => money(d.total_amount, d.currency) },
              { key: "imported", label: "Origin", value: (d) => (d.imported ? "imported" : "created here"),
                render: (d) => (d.imported ? <Pill>imported</Pill> : <span className="muted">created here</span>) },
            ]} />
        )}
      </Card>
    </>
  );
}
